/**
 * Copyright © 2025-2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { MsalProvider } from "@azure/msal-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactDOM from "react-dom/client";

import msalInstance from "./features/azure-b2c/msalInstance";
import Analytics from "./services/analytics";
import { cachePurge } from "./util/helpers";
import App from "./App";
import { reportHandler } from "./reportHandler";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";

import "./styles/tailwind.css";
import "./styles/root.scss";

dayjs.extend(relativeTime);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
Analytics.initialize();
cachePurge();

const supportsDvh = () =>
  typeof window.CSS !== "undefined" && window.CSS.supports?.("height", "100dvh");

const documentHeight = () => {
  let scale = 1;

  // Check if the window height is 720 pixels or less
  if (window.innerHeight <= 720 && window.innerWidth > 1200) {
    scale = 0.75; // 720p is 75% of 1080p
  }

  const rootEl = document.getElementById("root");
  const doc = document.documentElement;
  const adjustedHeight = window.innerHeight / scale;
  const useDvh = scale === 1 && supportsDvh();

  if (rootEl) {
    rootEl.style.transform = `scale(${scale})`;
    rootEl.style.transformOrigin = "top left";
    rootEl.style.width = `${100 / scale}vw`;
    rootEl.style.height = useDvh ? "100dvh" : `${100 / scale}vh`;
  }

  doc.style.setProperty("--doc-height", useDvh ? "100dvh" : `${adjustedHeight}px`);
};

window.addEventListener("resize", documentHeight);
documentHeight();

root.render(
  <MsalProvider instance={msalInstance}>
    <App />
  </MsalProvider>
);

// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(log) from ./util/log)
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(reportHandler);
